/**
 * The sign-in screen, and the route guard in front of the app.
 *
 * Styled in the existing design system rather than as a generic login page: the
 * Christmas tree is the product's signature (§8), and it is the one screen every
 * user sees before anything else.
 */

import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { TriangleAlert } from 'lucide-react';

import { Tree } from '../components/primitives';
import { Footer } from '../components/Footer';
import { Landing } from '../screens/Landing';
import { isReady, renderSignInButton } from './gis';
import { useAuth } from './AuthProvider';

/**
 * Google's button, in a box React must not touch.
 *
 * GIS renders an iframe into this node and owns its subtree from then on. React
 * needs to know nothing lives inside — hence the empty div and the ref. If
 * React ever re-rendered children into it, it would either wipe Google's iframe
 * or throw trying to remove a node it did not create.
 */
function GoogleButton() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current && isReady()) renderSignInButton(ref.current);
  });

  return <div className="gsi-slot" ref={ref} />;
}

/**
 * The sign-in control itself — the Google button, or the honest explanation of
 * why there isn't one.
 *
 * Shared by the sign-in card and the landing page so the GIS wiring has exactly
 * one home. Only ever one of them is mounted at a time, which matters: two
 * `gsi-slot`s alive at once would have GIS render into whichever it saw last.
 */
export function SignInAction() {
  const { error, isLoading } = useAuth();

  if (error) {
    /**
     * A configuration failure is not the user's fault and no amount of
     * clicking fixes it, so say so plainly rather than showing a button
     * that cannot work. The likeliest cause by far is a missing
     * `VITE_GOOGLE_CLIENT_ID` at build time.
     */
    return (
      <div className="signin-error">
        <TriangleAlert size={15} />
        <div>
          <b>Sign-in unavailable</b>
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="signin-actions">
      <GoogleButton />
      {isLoading && <div className="signin-wait">Checking for an existing session…</div>}
    </div>
  );
}

export function SignIn() {
  return (
    <div className="svcdue signin-page">
      <div className="signin-card">
        <div className="brand signin-brand">
          <span className="brand-mark">
            <span className="bm-b bm-a" />
            <span className="bm-b bm-a" />
            <span className="bm-b bm-g" />
          </span>
          <div className="brand-txt">
            <b>SvcDue</b>
            <span>race maintenance</span>
          </div>
        </div>

        <div className="signin-tree">
          <Tree animate />
        </div>

        <h1 className="h1 signin-h">Know what's due before you load the trailer.</h1>
        <p className="lede signin-lede">
          Track wear parts, safety gear and inspections across your fleet. Sign in with Google to open your garage.
        </p>

        <SignInAction />

        <p className="signin-fine">
          We store only your name and email. You can delete your account and its data at any time.
        </p>
      </div>

      <Footer />
    </div>
  );
}

/**
 * The auth guard (§8).
 *
 * `isLoading` gets its own branch rather than falling through to `SignIn`. The
 * restore from IndexedDB is asynchronous, so a returning user is briefly
 * "not signed in" — rendering the sign-in screen during that window would flash
 * it up and yank it away on every single page load. This is the whole reason
 * `gis.ts` restores from storage before it touches Google.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { isSignedIn, isLoading } = useAuth();
  const { pathname } = useLocation();

  if (isLoading) return <BootSplash />;

  /**
   * The root gets the landing page, everything else the compact card.
   *
   * A stranger typing svcdue.com deserves to know what this is before being
   * asked to sign in. Someone who followed a link to /garage or /timeline has
   * already decided — showing them a marketing page instead of a sign-in button
   * would be an obstacle, not an introduction.
   */
  if (!isSignedIn) return pathname === '/' ? <Landing /> : <SignIn />;
  return <>{children}</>;
}

export function BootSplash() {
  return (
    <div className="svcdue signin-page">
      <div className="signin-card">
        <div className="signin-tree">
          <Tree />
        </div>
        <div className="signin-wait">Loading…</div>
      </div>
    </div>
  );
}
